import { useDispatch, useSelector } from 'react-redux';

import { requestSelectors } from 'src/store/requests/selectors';
import { IAppTranslationData, ITranslationData } from 'src/store/requests/translations/reducer';
import { useCallback, useState } from 'react';
import {
  getCurrentMarketplace,
  getMarketplaceComplexTranslation,
  getMarketplaceContactFormComplex,
} from 'src/store/marketplace/selectors';
import { getMarketplaceTranslationRequestData } from 'src/api/marketplace';
import { Actions } from 'src/store/marketplace/actions';
import { getAppTranslationRequestData } from 'src/api';

export const useTranslations = (keyNameFixed?: string) => {
  const translationsData: ITranslationData | null = useSelector(requestSelectors.translations.getTranslationData);
  const marketplaceTranslations = useSelector(getMarketplaceComplexTranslation);
  const formComplex = useSelector(getMarketplaceContactFormComplex);
  const marketplace = useSelector(getCurrentMarketplace);
  const [appTranslationsData, setAppTranslationsData] = useState<{[key: string]: IAppTranslationData}>({});
  const dispatch = useDispatch();

  const fetchTranslations = useCallback(async (complexId: string | undefined) => {
    if (marketplace && complexId) {
      const translations = await getMarketplaceTranslationRequestData(complexId, marketplace.language);
      return translations;
    }
    return undefined;
  }, [marketplace]);

  const fetchMarketplaceTranslations = useCallback(() => {
    if (formComplex && marketplace) {
      fetchTranslations(formComplex.id).then((response) => {
        dispatch(Actions.setMarketplaceTranslations(response?.data));
      });
    }
  }, [marketplace, formComplex]);

  const generateTranslations = useCallback((keyName: string): string => {
    if (marketplaceTranslations || translationsData) {
      if (marketplaceTranslations?.[keyName] || translationsData?.[keyName]) {
        return marketplaceTranslations?.[keyName] || translationsData?.[keyName] || keyName;
      }

      return keyName;
    }

    return keyName;
  }, [marketplaceTranslations, translationsData]);

  const getTranslations = useCallback((keyName: string) => {
    return generateTranslations(keyName);
  }, [marketplaceTranslations, generateTranslations]);

  const fetchAppTranslations = useCallback(() => {
    return getAppTranslationRequestData().then((response) => {
      const data: {[key: string]: IAppTranslationData} = {};
      response?.data['hydra:member'].forEach((item) => {
        data[item.systemName] = item;
      });
      setAppTranslationsData(data);
    });
  }, [marketplaceTranslations, generateTranslations]);

  const getAppTranslationBySystemName = useCallback((systemName: string) => {
    return appTranslationsData[systemName];
  }, [appTranslationsData]);

  const getFixedTranslations = useCallback(() => {
    if (keyNameFixed) {
      return generateTranslations(keyNameFixed);
    }

    return '';
  }, [generateTranslations, keyNameFixed]);

  return {
    getTranslations,
    fetchAppTranslations,
    getAppTranslationBySystemName,
    getFixedTranslations,
    fetchMarketplaceTranslations,
    appTranslationsData,
    marketplaceTranslations,
  };
};
